import {ReactNode, useContext, useEffect, useMemo, useState} from "react";
import {SquadContext} from "./useSquadContextProvider";
import useWebSocketServer, {Rooms} from "./useWebSocketServer";
import {iMember, iSquad, StatusMember} from "./useSquadInterfaces";


const createId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const SquadProvider = ({ children }: { children: ReactNode }) => {

	const { users, Actions, RoomID, StoreData } = useWebSocketServer()

	const [squads, setSquads] = useState<iSquad[]>([])

	const players = useMemo( () =>
			users[RoomID ?? Rooms.GLOBAL] ?? []
		, [users, RoomID] )

	useEffect(() => {
		console.log("SquadProvider Users", users )
	}, [users,]);

	useEffect(() => {
		console.log("SquadProvider Players", players )
	}, [players]);

	useEffect(() => {
		console.log("SquadProvider StoreData", StoreData )
		if( JSON.stringify( StoreData ) !== JSON.stringify( squads ) )
			setSquads( StoreData || [] )
	}, [StoreData]);

	const _Actions = {
		instance: {
			create: () => {
				Actions.Room.create()
			},
			join: ( uuid: string ) => {
				Actions.Room.join( uuid )
			},
			leave: () => {
				Actions.Room.leave()
			}
		},
		squads: {
			create: (name: string, callSign: string) => {
				if (!name.trim() || !callSign.trim())
					return;

				Actions.Room.storeData(
					[ ...squads, {
						id: createId(),
						name: name,
						callSign: callSign,
						objectives: [
							{name: "Team versammelt", completed: false},
							{name: "Mission erklärt", completed: false},
							{name: "Team bereit", completed: false},
						],
						specialActions: [{name: "emergencySupportRequired", active: false}],
						members: []
				}])
			},
			delete: (squadId: string) => {
				const newValues = squads.filter(squad => squad.id !== squadId)

				Actions.Room.storeData( newValues )
			},
			join: (squadId: string, member: iMember) => {
				const newValue = squads
					.map(squad => ({
						...squad,
						members: squad.members.filter(squadMember => squadMember.id !== member.id)
					})).map(squad =>
						squad.id === squadId
							? {
								...squad,
								members: [ ...squad.members, { ...member, status: StatusMember.alive } ]
							}
							: squad
					)

				Actions.Room.storeData( newValue )
			},
			leave: ( memberId: string ) => {
				const newValue = squads.map(squad => ({
						...squad,
						members: squad.members.filter(member => member.id !== memberId)
					}))

				Actions.Room.storeData( newValue )
			},
			update: (squadId: string, name: string, callSign: string) => {
				const trimmedName = name.trim();
				const trimmedCallSign = callSign.trim();

				if (!trimmedName || !trimmedCallSign)
					return;

				const newValues = squads.map(squad =>
					squad.id === squadId
						? {
							...squad,
							name: trimmedName,
							callSign: trimmedCallSign
						}
						: squad
				)

				Actions.Room.storeData( newValues )
			},
			updateMemberStatus: (memberId: string, status: StatusMember) => {
				const newValue = squads.map(squad => ({
						...squad,
						members: squad.members.map(member =>
							member.id === memberId
								? { ...member, status }
								: member
						)
					}))
				Actions.Room.storeData( newValue )
			},
			toggleSupport: (squadId: string) => {
				const newValue = squads.map( squad =>
					squad.id === squadId
						? {
							...squad,
							specialActions: squad.specialActions.map(action =>
								action.name === "emergencySupportRequired"
									? {
										...action,
										active: !action.active
									}
									: action
							)
						}
						: squad
				)
				console.log( "toggleSupport", newValue )
				Actions.Room.storeData( newValue )
			},
			addObjective: (squadId: string, name: string) => {
				if (!name.trim())
					return;

				const newValues = squads.map(squad =>
						squad.id === squadId
							? {
								...squad,
								objectives: [ ...squad.objectives, { name, completed: false } ]
							}
							: squad
					)
				Actions.Room.storeData( newValues )
			},
			toggleObjective: (squadId: string, objectiveName: string) => {
				const newValues = squads.map(squad =>
						squad.id === squadId
							? {
								...squad,
								objectives:
									squad.objectives.map(objective =>
										objective.name === objectiveName
										? { ...objective, completed: !objective.completed }
										: objective
									)
							}
							: squad
					)
				Actions.Room.storeData( newValues )
			},
			deleteObjective: (squadId: string, objectiveName: string) => {
				const newValues = squads.map(squad =>
					squad.id === squadId
						? {
							...squad,
							objectives: squad.objectives.filter(
								objective => objective.name !== objectiveName
							)
						}
						: squad
				)

				Actions.Room.storeData( newValues )
			}
		}
	}

	return (
		<SquadContext.Provider value={{
			users: players,
			squads: squads,
			instanceId: RoomID === Rooms.GLOBAL ? undefined : RoomID,
			Actions: {
				instance: _Actions.instance,
				squads: _Actions.squads
			}
		}}>
			{children}
		</SquadContext.Provider>
	)
}

export default () => useContext( SquadContext )